import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRoleStore } from "@/lib/store";
import { signIn, signUp, twoFactor } from "@/lib/auth-client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, ShieldCheck, Fingerprint, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

type AuthMode = "login" | "register" | "2fa";

export function LoginPage() {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setRole = useRoleStore((s) => s.setRole);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email and password required"); return; }
    setLoading(true);
    try {
      const res = await signIn.email({ email, password });
      if (res.error) throw new Error(res.error.message || "Login failed");
      if ((res.data as any)?.twoFactorRedirect) {
        setMode("2fa");
        return;
      }
      onAuthSuccess(res.data?.user);
    } catch (err: any) {
      toast.error(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !name) { toast.error("All fields required"); return; }
    setLoading(true);
    try {
      const res = await signUp.email({ email, password, name });
      if (res.error) throw new Error(res.error.message || "Registration failed");
      onAuthSuccess(res.data?.user);
    } catch (err: any) {
      toast.error(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await twoFactor.verifyTotp({ code: totpCode });
      if (res.error) throw new Error(res.error.message || "Invalid 2FA code");
      onAuthSuccess(res.data?.user);
    } catch (err: any) {
      toast.error(err.message || "2FA verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    toast.info("Passkey sign-in is not enabled in the current Better Auth configuration yet.");
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await signIn.social({ provider: "google" });
    } catch (err: any) {
      toast.error(err.message || "Google sign-in failed");
      setLoading(false);
    }
  };

  const onAuthSuccess = (user: any) => {
    if (!user) return;
    const role = user.role || "Officer";
    setRole(role);
    toast.success(`Welcome, ${user.name}`);
    if (role === "Client") {
      navigate("/client/dashboard");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-accent/20 p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="mx-auto h-20 w-20 rounded-3xl bg-primary flex items-center justify-center shadow-glow-lg">
            <ShieldCheck className="text-primary-foreground h-12 w-12" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight uppercase">Perfect Match</h1>
            <p className="text-muted-foreground font-bold uppercase tracking-widest text-[10px] mt-1">Secure Financial Platform</p>
          </div>
        </div>

        <Card className="overflow-hidden rounded-3xl border border-border shadow-glass">
          <CardHeader className="border-b bg-muted/10 p-8">
            <CardTitle className="text-2xl font-black text-foreground uppercase tracking-tight">
              {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Two-Factor Auth"}
            </CardTitle>
            <CardDescription className="text-muted-foreground font-bold uppercase tracking-widest text-[10px] mt-1">
              {mode === "2fa" ? "Enter your authenticator code" : "Secure access to your account"}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-8 space-y-6">
            {/* 2FA Form */}
            {mode === "2fa" && (
              <form onSubmit={handle2FA} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest">Authenticator Code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className="h-14 text-center text-2xl font-mono tracking-[0.5em]"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    disabled={loading}
                  />
                </div>
                <Button type="submit" className="w-full h-14 font-black uppercase" disabled={loading || totpCode.length !== 6}>
                  {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <KeyRound className="mr-2 h-5 w-5" />}
                  Verify
                </Button>
                <Button variant="ghost" className="w-full" onClick={() => setMode("login")}>Back to login</Button>
              </form>
            )}

            {/* Login / Register Form */}
            {mode !== "2fa" && (
              <>
                <form onSubmit={mode === "login" ? handleEmailLogin : handleRegister} className="space-y-4">
                  {mode === "register" && (
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-widest">Full Name</Label>
                      <Input
                        placeholder="Your name"
                        className="h-12"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        className="pl-12 h-12"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest">Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      className="h-12"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <Button type="submit" className="w-full h-14 font-black bg-primary hover:bg-primary/90 uppercase" disabled={loading}>
                    {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ArrowRight className="mr-2 h-5 w-5" />}
                    {mode === "login" ? "Sign In" : "Create Account"}
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground font-bold">Or continue with</span>
                  </div>
                </div>

                {/* Passkey / Biometric */}
                <Button
                  variant="outline"
                  className="w-full h-12 font-bold"
                  onClick={handlePasskeyLogin}
                  disabled={loading}
                >
                  <Fingerprint className="mr-2 h-5 w-5" />
                  Passkey / Biometric
                </Button>

                {/* Google OAuth */}
                <Button
                  variant="outline"
                  className="w-full h-12 font-bold"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Google
                </Button>

                {/* Toggle login/register */}
                <p className="text-center text-sm text-muted-foreground">
                  {mode === "login" ? "No account?" : "Already have an account?"}
                  <button
                    className="ml-1 font-bold text-primary hover:underline"
                    onClick={() => setMode(mode === "login" ? "register" : "login")}
                  >
                    {mode === "login" ? "Register" : "Sign In"}
                  </button>
                </p>
              </>
            )}
          </CardContent>

          <CardFooter className="bg-muted/10 p-6 flex flex-col gap-2 text-center border-t">
            <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">Perfect Match Susu PLC • Secure Platform</p>
            <div className="flex justify-center gap-4 text-[9px] text-muted-foreground font-bold uppercase">
              <span>WebAuthn</span>
              <span>•</span>
              <span>TOTP 2FA</span>
              <span>•</span>
              <span>OAuth 2.0</span>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}